import i18n from '../../../i18n';
import type { ConnectedSourceId, GroupKpis, Outlet } from '../types';
import { SOURCES } from './sources';

export type MetricProvenance = 'measured' | 'inferred' | 'projected';

// Whether a value reflects this exact moment, the running total for the day,
// or a planned/typical figure. Surfaced on each tile so "now" vs "total"
// numbers are never confused.
export type MetricScope = 'now' | 'today' | 'planned';

export type MetricConfig = {
  label: string;
  short: string;
  unit?: string;
  icon: string;
  better: 'high' | 'low';
  target?: number;
  needs?: ConnectedSourceId;
  needsName?: string;
  ghost?: string;
  provenance?: MetricProvenance;
  scope?: MetricScope;
  get: (o: Outlet) => number;
  group: (k: GroupKpis) => number;
  fmt: (v: number) => string;
  agg?: 'sum';
};

export const SCOPE_META: Record<MetricScope, { label: string; hint: string }> =
  {
    now: {
      get label() {
        return i18n.t('metrics:scope.now.label');
      },
      get hint() {
        return i18n.t('metrics:scope.now.hint');
      },
    },
    today: {
      get label() {
        return i18n.t('metrics:scope.today.label');
      },
      get hint() {
        return i18n.t('metrics:scope.today.hint');
      },
    },
    planned: {
      get label() {
        return i18n.t('metrics:scope.planned.label');
      },
      get hint() {
        return i18n.t('metrics:scope.planned.hint');
      },
    },
  };

export const PROVENANCE_META: Record<
  MetricProvenance,
  { label: string; hint: string }
> = {
  measured: {
    get label() {
      return i18n.t('metrics:provenance.measured.label');
    },
    get hint() {
      return i18n.t('metrics:provenance.measured.hint');
    },
  },
  inferred: {
    get label() {
      return i18n.t('metrics:provenance.inferred.label');
    },
    get hint() {
      return i18n.t('metrics:provenance.inferred.hint');
    },
  },
  projected: {
    get label() {
      return i18n.t('metrics:provenance.projected.label');
    },
    get hint() {
      return i18n.t('metrics:provenance.projected.hint');
    },
  },
};

/** Number locale for the active UI language: Swiss German or British English. */
export function numberLocale(): 'de-CH' | 'en-GB' {
  return i18n.language?.startsWith('de') ? 'de-CH' : 'en-GB';
}

export const fmt = {
  num: (v: number) => Math.round(v).toLocaleString(numberLocale()),
  money: (v: number) => `€${v.toLocaleString(numberLocale())}`,
  // Compact money for dense tiles: €1.2k / €12k.
  eur: (n: number) =>
    n >= 1000
      ? `€${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
      : `€${Math.round(n)}`,
  // Minutes-since-midnight -> HH:MM.
  clock: (m: number) =>
    `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(
      Math.round(m) % 60,
    ).padStart(2, '0')}`,
};

/** Format money with a currency symbol/code from the payload (CHF, €, £, …). */
export function formatMoney(v: number, unit?: string | null): string {
  const n = Number.isFinite(v) ? v : 0;
  const u = (unit || '€').trim();
  const abs = Math.abs(n);
  const compact =
    abs >= 1000
      ? `${(n / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`
      : Number.isInteger(n)
        ? String(Math.round(n))
        : n.toLocaleString(numberLocale(), { maximumFractionDigits: 2 });
  // Symbol currencies prefix; ISO codes get a thin space (CHF 12.5k).
  if (u.length === 1 || u === '€' || u === '£' || u === '$') {
    return `${u}${compact}`;
  }
  return `${u}\u00a0${compact}`;
}

/** Format a drill-down value by unit (used when bucket unit differs from hero metric). */
export function formatDrillUnit(
  v: number,
  unit?: 'eur' | 'num' | 'pct' | 'pt' | 'min' | 'x' | 'perSeat' | string,
): string {
  switch (unit) {
    case 'eur':
      return fmt.eur(v);
    case 'pct':
      return `${v}%`;
    case 'pt':
      return `${v}pt`;
    case 'min':
      return `${v}m`;
    case 'x':
    case '×':
      return `${v}×`;
    case 'perSeat':
      // Turn correlates whose buckets would read 0.0x×: guests per seat.
      return i18n.t('performance:drillUi.perSeatValue', {
        value: v.toLocaleString(numberLocale(), { maximumFractionDigits: 2 }),
      });
    case 'num':
    case undefined:
    case '':
      if (Number.isInteger(v)) return v.toLocaleString(numberLocale());
      return v.toLocaleString(numberLocale(), { maximumFractionDigits: 1 });
    default:
      // POS currency codes / symbols (CHF, €, …).
      return formatMoney(v, unit);
  }
}

export function occupancyPct(o: {
  seated: number;
  seats: number;
  closed?: boolean;
}): number {
  if (o.closed) {
    return 0;
  }
  return Math.min(100, Math.round((o.seated / o.seats) * 100));
}

export const METRICS: Record<string, MetricConfig> = {
  occupancy: {
    get label() { return i18n.t('metrics:occupancy.label'); },
    get short() { return i18n.t('metrics:occupancy.short'); },
    unit: '%',
    icon: 'users',
    better: 'high',
    target: 80,
    provenance: 'measured',
    scope: 'now',
    get: o => occupancyPct(o),
    group: k => k.occupancy,
    fmt: v => `${v}%`,
  },
  brainAutoPct: {
    get label() { return i18n.t('metrics:brainAutoPct.label'); },
    get short() { return i18n.t('metrics:brainAutoPct.short'); },
    unit: '%',
    icon: 'table',
    better: 'high',
    provenance: 'measured',
    scope: 'today',
    get: () => 0,
    group: () => 0,
    fmt: v => `${v}%`,
  },
  covers: {
    get label() { return i18n.t('metrics:covers.label'); },
    get short() { return i18n.t('metrics:covers.short'); },
    icon: 'table',
    better: 'high',
    provenance: 'measured',
    scope: 'now',
    get: o => o.coversToday,
    group: k => k.covers,
    fmt: v => fmt.num(v),
    agg: 'sum',
  },
  reservations: {
    get label() { return i18n.t('metrics:reservations.label'); },
    get short() { return i18n.t('metrics:reservations.short'); },
    icon: 'calendar',
    better: 'high',
    provenance: 'measured',
    scope: 'today',
    get: o => o.bookedCount ?? 0,
    group: k => k.incoming,
    fmt: v => fmt.num(v),
    agg: 'sum',
  },
  rejected: {
    get label() { return i18n.t('metrics:rejected.label'); },
    get short() { return i18n.t('metrics:rejected.short'); },
    icon: 'ban',
    better: 'low',
    provenance: 'measured',
    scope: 'today',
    get: () => 0,
    group: () => 0,
    fmt: v => fmt.num(v),
    agg: 'sum',
  },
  incoming: {
    get label() { return i18n.t('metrics:incoming.label'); },
    get short() { return i18n.t('metrics:incoming.short'); },
    icon: 'calendar',
    better: 'high',
    provenance: 'measured',
    scope: 'today',
    get: o => o.upcoming,
    group: k => k.incoming,
    fmt: v => `+${v}`,
    agg: 'sum',
  },
  seatTime: {
    get label() { return i18n.t('metrics:seatTime.label'); },
    get short() { return i18n.t('metrics:seatTime.short'); },
    unit: 'min',
    icon: 'clock',
    better: 'low',
    target: 80,
    provenance: 'measured',
    scope: 'planned',
    get: o => o.avgSeatMin,
    group: k => k.seatTime,
    fmt: v => `${v}m`,
  },
  efficiency: {
    get label() { return i18n.t('metrics:efficiency.label'); },
    get short() { return i18n.t('metrics:efficiency.short'); },
    unit: 'PAX/tbl',
    icon: 'users',
    better: 'high',
    target: 2.4,
    provenance: 'measured',
    scope: 'now',
    // Same guard as the server getter (drill_math.METRIC_GETTERS): an open
    // outlet with nobody seated has occTables = 0, which would render NaN.
    get: o => +(o.seated / Math.max(o.occTables, 1)).toFixed(1),
    group: k => k.efficiency,
    fmt: v => String(v),
  },
  turns: {
    get label() { return i18n.t('metrics:turns.label'); },
    get short() { return i18n.t('metrics:turns.short'); },
    unit: '×',
    icon: 'refresh',
    better: 'high',
    target: 2.4,
    provenance: 'measured',
    scope: 'today',
    get: o => +(o.coversToday / o.seats).toFixed(2),
    group: k => k.turns,
    fmt: v => `${v}×`,
  },
  noshow: {
    get label() { return i18n.t('metrics:noshow.label'); },
    get short() { return i18n.t('metrics:noshow.short'); },
    unit: '%',
    icon: 'flag',
    better: 'low',
    target: 5,
    provenance: 'measured',
    scope: 'today',
    get: o => o.noShow,
    group: k => k.noshow,
    fmt: v => `${v}%`,
  },
  cancel: {
    get label() { return i18n.t('metrics:cancel.label'); },
    get short() { return i18n.t('metrics:cancel.short'); },
    unit: '%',
    icon: 'x',
    better: 'low',
    target: 6,
    provenance: 'measured',
    scope: 'today',
    get: o => o.cancelRate,
    group: k => k.cancel,
    fmt: v => `${v}%`,
  },
  sellitems: {
    get label() { return i18n.t('metrics:sellitems.label'); },
    get short() { return i18n.t('metrics:sellitems.short'); },
    icon: 'euro',
    better: 'high',
    needs: 'alenopay',
    needsName: 'aleno Pay',
    ghost: '€1.2k',
    provenance: 'measured',
    scope: 'today',
    get: o => o.sellRevenue ?? 0,
    group: k => k.sellRevenue,
    fmt: v => fmt.eur(v),
    agg: 'sum',
  },
  northstar: {
    get label() { return i18n.t('metrics:northstar.label'); },
    get short() { return i18n.t('metrics:northstar.short'); },
    icon: 'star',
    better: 'high',
    provenance: 'measured',
    scope: 'today',
    get: () => 0,
    group: () => 0,
    fmt: v => String(v),
  },
  revenue: {
    get label() { return i18n.t('metrics:revenue.label'); },
    get short() { return i18n.t('metrics:revenue.short'); },
    icon: 'euro',
    better: 'high',
    needs: 'pos',
    needsName: 'POS',
    ghost: '€243,957',
    scope: 'today',
    get: o => o.coversToday * (o.spendCover ?? 0),
    group: k => k.revenue,
    fmt: v => fmt.money(v),
    agg: 'sum',
  },
  avgCheck: {
    get label() { return i18n.t('metrics:avgCheck.label'); },
    get short() { return i18n.t('metrics:avgCheck.short'); },
    icon: 'receipt',
    better: 'high',
    needs: 'pos',
    needsName: 'POS',
    ghost: '€69',
    provenance: 'measured',
    get: o => o.spendCover ?? 0,
    group: k => k.spend,
    fmt: v => formatMoney(v, '€'),
  },
  revpash: {
    get label() { return i18n.t('metrics:revpash.label'); },
    get short() { return i18n.t('metrics:revpash.short'); },
    icon: 'banknote',
    better: 'high',
    needs: 'pos',
    needsName: 'POS',
    ghost: '€12',
    provenance: 'measured',
    get: () => 0,
    group: () => 0,
    fmt: v => formatMoney(v, '€'),
  },
  spend: {
    get label() { return i18n.t('metrics:spend.label'); },
    get short() { return i18n.t('metrics:spend.short'); },
    unit: '€',
    icon: 'euro',
    better: 'high',
    needs: 'pos',
    needsName: 'POS',
    ghost: '€69',
    get: o => o.spendCover ?? 0,
    group: k => k.spend,
    fmt: v => `€${v}`,
  },
  sentiment: {
    get label() { return i18n.t('metrics:sentiment.label'); },
    get short() { return i18n.t('metrics:sentiment.short'); },
    unit: '★',
    icon: 'star',
    better: 'high',
    needs: 'reviews',
    needsName: 'Reviews',
    ghost: '4.34★',
    target: 4.2,
    get: o => o.review ?? 0,
    group: k => k.review,
    fmt: v => `${v}★`,
  },
};

// Single source of truth for what each Performance metric actually counts, so
// every figure on the dashboard can state its unit. Guests (the sum of party
// sizes) and reservations (booking count) are different magnitudes and were
// easy to conflate; `noun` is rendered next to the value and `tip` explains
// the definition on hover. An empty `noun` means the unit (%, m, €, ×) already
// makes the figure unambiguous. Internal metric key remains `covers`.
export const PERF_MEASURE: Record<string, { noun?: string; tip: string; icon?: string; connectSource?: ConnectedSourceId }> = {
  covers: {
    get noun() { return i18n.t('metrics:measure.covers.noun'); },
    get tip() { return i18n.t('metrics:measure.covers.tip'); },
    icon: 'utensils',
  },
  reservations: {
    get noun() { return i18n.t('metrics:measure.reservations.noun'); },
    get tip() { return i18n.t('metrics:measure.reservations.tip'); },
    icon: 'calendarCheck',
  },
  rejected: {
    get noun() { return i18n.t('metrics:measure.rejected.noun'); },
    get tip() { return i18n.t('metrics:measure.rejected.tip'); },
    icon: 'ban',
  },
  noshow: {
    get noun() { return i18n.t('metrics:measure.noshow.noun'); },
    get tip() { return i18n.t('metrics:measure.noshow.tip'); },
    icon: 'userX',
  },
  cancel: {
    get noun() { return i18n.t('metrics:measure.cancel.noun'); },
    get tip() { return i18n.t('metrics:measure.cancel.tip'); },
    icon: 'calendarX',
  },
  turns: {
    get noun() { return i18n.t('metrics:measure.turns.noun'); },
    get tip() { return i18n.t('metrics:measure.turns.tip'); },
    icon: 'refresh',
  },
  occupancy: {
    get noun() { return i18n.t('metrics:measure.occupancy.noun'); },
    get tip() { return i18n.t('metrics:measure.occupancy.tip'); },
    icon: 'pieChart',
  },
  brainAutoPct: {
    get noun() { return i18n.t('metrics:measure.brainAutoPct.noun'); },
    get tip() { return i18n.t('metrics:measure.brainAutoPct.tip'); },
    icon: 'table',
  },
  seatTime: {
    get tip() { return i18n.t('metrics:measure.seatTime.tip'); },
    icon: 'hourglass',
  },
  sellitems: {
    get tip() { return i18n.t('metrics:measure.sellitems.tip'); },
    icon: 'shoppingBag',
    connectSource: 'alenopay',
  },
  avgCheck: {
    get tip() { return i18n.t('metrics:measure.avgCheck.tip'); },
    icon: 'receipt',
    connectSource: 'pos',
  },
  revpash: {
    get tip() { return i18n.t('metrics:measure.revpash.tip'); },
    icon: 'banknote',
    connectSource: 'pos',
  },
  revenue: {
    get tip() { return i18n.t('metrics:measure.revenue.tip'); },
    icon: 'banknote',
    connectSource: 'pos',
  },
};

const CONNECT_CTA_KEYS: Partial<Record<ConnectedSourceId, string>> = {
  pos: 'metrics:connect.pos',
  reviews: 'metrics:connect.reviews',
  alenopay: 'metrics:connect.alenopay',
  weather: 'metrics:connect.weather',
  events: 'metrics:connect.events',
  demandgen: 'metrics:connect.demandgen',
};

export function perfConnectSource(metricKey: string): ConnectedSourceId | undefined {
  return PERF_MEASURE[metricKey]?.connectSource;
}

/** Scorecard / drill title from the UI catalog — never the English payload label. */
export function metricDisplayLabel(key: string, fallback?: string): string {
  const perfKey = `performance:metrics.${key}`;
  const fromPerf = i18n.t(perfKey);
  if (fromPerf && fromPerf !== perfKey && fromPerf !== `metrics.${key}`) {
    return fromPerf;
  }
  const fromMetrics = METRICS[key]?.label;
  if (fromMetrics) return fromMetrics;
  return fallback || key;
}

const POS_MONEY_KEYS = new Set(['revenue', 'avgCheck', 'revpash', 'spend']);

/** Drill hero / series formatter — uses payload currency for POS money keys. */
export function formatDrillMetric(
  v: number,
  metricKey: string,
  unit?: string | null,
): string {
  if (metricKey === 'sellitems') return formatMoney(v, '€');
  if (POS_MONEY_KEYS.has(metricKey)) return formatMoney(v, unit || '€');
  const m = METRICS[metricKey];
  if (m) return m.fmt(v);
  return formatDrillUnit(v, unit || 'num');
}

export function connectCta(sourceId: ConnectedSourceId): string {
  const key = CONNECT_CTA_KEYS[sourceId];
  if (key) return i18n.t(key);
  return i18n.t('metrics:connect.fallback', {
    name: SOURCES.find(s => s.id === sourceId)?.name ?? sourceId,
  });
}

// The noun for a metric's value, if one is needed to disambiguate it.
export function perfMeasureNoun(metricKey: string): string | undefined {
  return PERF_MEASURE[metricKey]?.noun;
}

export const METRIC_ORDER = [
  'occupancy',
  'covers',
  'incoming',
  'turns',
  'seatTime',
  'efficiency',
  'noshow',
  'cancel',
  'sellitems',
  'revenue',
  'spend',
  'sentiment',
];

const clamp01 = (x: number) =>
  Math.max(0, Math.min(1, Number.isFinite(x) ? x : 0));

export function outletTurns(o: Outlet): number {
  return o.seats > 0 ? +(o.coversToday / o.seats).toFixed(2) : 0;
}

// Composite fleet score: live occupancy, day throughput, and reliability.
// Occupancy is weighted heavily - higher fill is better. Always finite.
export function perfScore(o: Outlet, connected: ConnectedSourceId[]): number {
  const turns = outletTurns(o);
  const occ = occupancyPct(o) / 100;
  const parts = [
    { w: 1.2, n: clamp01(occ) },
    { w: 1.2, n: clamp01(turns / 2.5) },
    { w: 1.0, n: clamp01(1 - o.noShow / 12) },
    { w: 0.8, n: clamp01(1 - o.cancelRate / 14) },
  ];
  if (connected.includes('reviews') && o.review) {
    parts.push({ w: 1.0, n: clamp01((o.review - 3) / 2) });
  }
  if (connected.includes('pos') && o.spendTrend !== undefined) {
    parts.push({ w: 0.8, n: clamp01((o.spendTrend + 14) / 28) });
  }
  const denom = parts.reduce((a, p) => a + p.w, 0) || 1;
  const score = parts.reduce((a, p) => a + p.w * p.n, 0) / denom;
  return Math.round(clamp01(score) * 100);
}

export type PerformanceFactorChip = {
  label: string;
  tone: 'win' | 'risk' | 'muted';
};

export type PerformanceAverages = {
  occupancy: number;
  turns: number;
  noShow: number;
  cancel: number;
};

export function poolPerformanceAverages(pool: Outlet[]): PerformanceAverages {
  const n = pool.length || 1;
  return {
    occupancy: pool.reduce((a, o) => a + occupancyPct(o), 0) / n,
    turns: pool.reduce((a, o) => a + outletTurns(o), 0) / n,
    noShow: pool.reduce((a, o) => a + o.noShow, 0) / n,
    cancel: pool.reduce((a, o) => a + o.cancelRate, 0) / n,
  };
}

export function formatPerformanceAverages(avg: PerformanceAverages) {
  return {
    occupancy: Math.round(avg.occupancy),
    turns: +avg.turns.toFixed(2),
    noShow: +avg.noShow.toFixed(1),
    cancel: +avg.cancel.toFixed(1),
  };
}

function factorTone(
  value: number,
  avg: number,
  better: 'high' | 'low',
  absBand: number,
): PerformanceFactorChip['tone'] {
  const band = Math.max(Math.abs(avg) * 0.15, absBand);
  if (better === 'high') {
    if (value > avg + band) return 'win';
    if (value < avg - band) return 'risk';
    return 'muted';
  }
  if (value < avg - band) return 'win';
  if (value > avg + band) return 'risk';
  return 'muted';
}

export function outletPerformanceFactors(
  o: Outlet,
  averages: PerformanceAverages,
): PerformanceFactorChip[] {
  const turns = outletTurns(o);
  const occ = occupancyPct(o);
  return [
    {
      label: i18n.t('metrics:factor.occupancy', { value: occ }),
      tone: factorTone(occ, averages.occupancy, 'high', 5),
    },
    {
      label: i18n.t('metrics:factor.turns', { value: turns }),
      tone: factorTone(turns, averages.turns, 'high', 0.15),
    },
    {
      label: i18n.t('metrics:factor.noshow', { value: o.noShow }),
      tone: factorTone(o.noShow, averages.noShow, 'low', 2),
    },
    {
      label: i18n.t('metrics:factor.cancels', { value: o.cancelRate }),
      tone: factorTone(o.cancelRate, averages.cancel, 'low', 2),
    },
  ];
}

export function outletSignals(
  o: Outlet,
  connected: ConnectedSourceId[],
  averages: PerformanceAverages,
) {
  return {
    score: perfScore(o, connected),
    factors: outletPerformanceFactors(o, averages),
  };
}

export function aggregateOutlets(
  outlets: Outlet[],
  connected: ConnectedSourceId[],
): GroupKpis & { has: (k: string) => boolean } {
  const open = outlets.filter(o => !o.closed);
  const n = open.length || 1;
  const seats = open.reduce((a, o) => a + o.seats, 0);
  const seated = open.reduce((a, o) => a + o.seated, 0);
  const tables = open.reduce((a, o) => a + o.tables, 0);
  const occTables = open.reduce((a, o) => a + o.occTables, 0);
  const covers = open.reduce((a, o) => a + o.coversToday, 0);
  const incoming = open.reduce((a, o) => a + o.upcoming, 0);
  const withSeatTime = open.filter(o => o.seated > 0 && o.avgSeatMin > 0);
  const seatTime = withSeatTime.length
    ? Math.round(
        withSeatTime.reduce((a, o) => a + o.avgSeatMin, 0) /
          withSeatTime.length,
      )
    : 0;
  const noshow = +(open.reduce((a, o) => a + o.noShow, 0) / n).toFixed(1);
  const cancel = +(open.reduce((a, o) => a + o.cancelRate, 0) / n).toFixed(1);
  const reviewMom = +(
    open.reduce((a, o) => a + (o.reviewMomentum ?? 0), 0) / n
  ).toFixed(2);
  const eff = +(seated / (occTables || 1)).toFixed(2);
  const revenue = open.reduce(
    (a, o) => a + o.coversToday * (o.spendCover ?? 0),
    0,
  );
  const spend = Math.round(
    open.reduce((a, o) => a + (o.spendCover ?? 0), 0) / n,
  );
  const review = +(
    open.reduce((a, o) => a + (o.review ?? 0), 0) / n
  ).toFixed(2);
  const web = open.reduce((a, o) => a + (o.webSessions ?? 0), 0);
  const turns = +(covers / (seats || 1)).toFixed(2);
  const sellRevenue = +open
    .reduce((a, o) => a + (o.sellRevenue ?? 0), 0)
    .toFixed(2);
  const sellCount = open.reduce((a, o) => a + (o.sellCount ?? 0), 0);
  const sellAttach = covers ? +((sellCount / covers) * 100).toFixed(1) : 0;

  const kpis: GroupKpis = {
    occupancy: Math.min(100, Math.round((seated / (seats || 1)) * 100)),
    seats,
    seated,
    tables,
    occTables,
    covers,
    incoming,
    seatTime,
    noshow,
    efficiency: eff,
    turns,
    cancel,
    reviewMom,
    revenue,
    spend,
    review,
    web,
    sellRevenue,
    sellCount,
    sellAttach,
    outlets: n,
  };

  return {
    ...kpis,
    has: (k: string) =>
      connected.some(c =>
        (['reservations', 'tableplan'].includes(c)
          ? ['occupancy', 'covers', 'incoming', 'seatTime', 'efficiency', 'noshow', 'cancel', 'turns']
          : c === 'pos'
            ? ['revenue', 'spend']
            : c === 'reviews'
              ? ['sentiment']
              : c === 'alenopay'
                ? ['sellitems']
                : []
        ).includes(k),
      ) || !METRICS[k]?.needs,
  };
}
